import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase } from 'app/shared/lib/essette-site';
import { bindable } from 'aurelia-typed-observable-plugin';
import { openAuthSummary } from 'app/um/components/summary/auth-summary';
import { CustomViewService, CustomViewReference } from 'app/shared/services/custom-view-service';
import { IWizardStep } from 'app/core/components/wizard/interfaces';
import { AuthService } from 'app/um/services/auth-service';
import { apiCall } from 'app/shared/lib/api/api';
import { AuthPortalPageContext } from 'app/pp/lib/auth-portal-page-context';
import { deepClone } from 'app/shared/lib/utilities';
import { focusFirst } from 'app/shared/lib/focus';
import { EventAggregator } from 'aurelia-event-aggregator';
import { AuthReloadEvent } from 'app/um/lib/events';
import { IApiActionResults } from 'app/shared/lib/api/api-types';
import { DateTimeValue } from 'app/shared/lib/date-time';
import * as UM from 'app/um/models/essette-api';

interface IClaimsSystemResults extends IApiActionResults {
    sentToClaimsSystem?: boolean;
}

export interface ICallFnIntervalTimer {
    completed: boolean;
    initiated: boolean;
    currentMS: number;
    previousMS: number;
    intervalMS: number;
    maxIntervalMS: number;
}

@autoinject()
export class ConfirmationStepCustomElement extends ComponentBase implements IWizardStep {

    ////****************** Fields and Properties ************************************////
    @bindable.object({ defaultBindingMode: bindingMode.twoWay })
    protected auth: UM.Auth = {};

    @bindable.stringNull({ defaultBindingMode: bindingMode.twoWay })
    public memberPlanId: string | null = null;

    @bindable protected scope: string = '';
    @bindable hideButtons: boolean = false;

    protected showAuthSummaryButton: boolean = false;
    protected openAuthSummary = openAuthSummary;
    public canSkip: boolean = false;
    public canContinue: boolean = true;
    public canPerformFinalSubmit: boolean = false;
    protected finalizeResponse: IClaimsSystemResults | null = null;

    public views: CustomViewReference[] = [];

    protected determinations: ReadonlyArray<UM.AuthDeterminationHistory> = [];

    protected callApiIntervalTimer: ICallFnIntervalTimer = {
        currentMS: 0,
        previousMS: 0,
        completed: false,
        initiated: false,
        intervalMS: 400, // starting interval. Afterwards previousMS + currentMS
        maxIntervalMS: this.env.config.apiPolling.maxIntervalMilliseconds
    };

    ////****************** Element References ***************************************////



    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private custom: CustomViewService,
        private authService: AuthService,
        protected readonly context: AuthPortalPageContext,
        private readonly events: EventAggregator,
        private $element: Element,
        ...rest: any[]) {
        super(...rest);
        this.logger.source = this;
    }

    protected bind(): void {
        this.checkPermissions();
    }

    protected attached(): void {
        this.loadCustomViews();
    }

    ////****************** Events ***************************************************////

    ////****************** Functions ************************************************////

    public async reload(reloadContext?: boolean): Promise<void> {
        if(!!reloadContext && this.auth.id) {
            await this.context.refreshAuthSettings({ id: this.auth.id });
        }

        this.auth = deepClone(this.context.auth);
    }

    public skipStep(): void { }
    public continueStep(): void { }

    public async openStep(): Promise<void> {
        //finalize the auth: flip pending flag, check for unanimous status
        //only call if we are still pending
        if(this.auth.isPending) {
            await this.finalizeAuth();
            if(!!this.finalizeResponse?.sentToClaimsSystem) {

                this.callApiIntervalTimer.completed = false;
                this.callApiIntervalTimer.initiated = false;
                this.checkLatestTransaction();
            }
        } else if(this.auth.id) {
            // Reload to get the latest status for a non-pending auth (as in update-auth-request workflow)
            await this.reload(true);
        }
        setTimeout(() => focusFirst(this.$element), 250); //view
    }

    public async closeStep(): Promise<void> { }
    public async finalSubmit(): Promise<void> { }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.showAuthSummaryButton = this.accountContext.hasRouteAccess('pp.auth.summary');
    }

    private async loadCustomViews(): Promise<void> {
        const baseUrl = 'app/pp/components/submit/';
        const key = `pp.confirmation.${this.scope}`;
        this.views = await this.custom.resolveCustomViewUrls(baseUrl, key, this.env.tenant.code);
    }

    protected async newRequest(): Promise<void> {
        this.memberPlanId = null;
        this.auth = {};
        this.router.navigate(`#/auths/create`);
    }

    protected async checkLatestTransactionCompleted(): Promise<void> {
        if(this.auth.id) {
            const response = await this.authService.loadLatestEagleTransactionByAuth(this.auth.id);
            const latestEagleTransaction = response.results[0];

            const completed = latestEagleTransaction.flag?.isComplete;
            const timestamp = latestEagleTransaction.metadata?.created?.timestamp;
            const eagleTimeout = this.env.config.eagleAuthClaimSyncTimeoutSeconds;

            // Check if timestamp is unavailable or if marked as completed.
            if(!timestamp || completed) {
                await this.reload(true)

                this.callApiIntervalTimer.completed = true;
                this.events.publish(new AuthReloadEvent());
                return;
            }

            let timestampDate = new Date(timestamp);
            timestampDate.setSeconds(timestampDate.getSeconds() + eagleTimeout);

            const transactionExpirationTimestamp = DateTimeValue.fromNativeDate(new Date(timestampDate));
            const now = DateTimeValue.fromNativeDate(new Date(this.context.timestamp ?? this.env.now()));

            // Api repeat timer has expired.
            if(now.isAfter(transactionExpirationTimestamp)) {
                await this.reload(true)
                this.callApiIntervalTimer.completed = true;
                this.events.publish(new AuthReloadEvent());
                return;
            }
        }
    }



    protected checkLatestTransaction(): void {
        const latestEagleTransaction = this.context.latestEagleTransaction;
        if(latestEagleTransaction?.id) {

            const completed = latestEagleTransaction.flag?.isComplete;

            if(!completed && !this.callApiIntervalTimer.initiated) {
                this.delayedRepeat(this.checkLatestTransactionCompleted, this.callApiIntervalTimer);
            }
        }
    }

    protected async delayedRepeat(fn: Function, timer: ICallFnIntervalTimer): Promise<void> {
        const initiated = timer.initiated;
        const intervalMS = timer.intervalMS;
        const maxMS = timer.maxIntervalMS;
        let previousMS = timer.previousMS;
        let currentMS = timer.currentMS;

        if(initiated === false) {
            timer.initiated = true;
            timer.previousMS = 0;
            timer.currentMS = 0;
        } else {
            currentMS = (currentMS <= 0) ? intervalMS : currentMS;
            const newTimeoutMS = previousMS + currentMS;
            timer.previousMS = currentMS;
            timer.currentMS = Math.min(newTimeoutMS, maxMS);
        }

        return new Promise((resolve) => {
            setTimeout(async () => {
                const thisFn = fn.bind(this);
                resolve(await thisFn());
                if(!timer.completed) {
                    this.delayedRepeat(fn, timer);
                } else {
                    timer.initiated = false;
                    timer.completed = false;
                    timer.previousMS = 0;
                    timer.currentMS = 0;
                }
            }, timer.currentMS);
        })
    }


    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadDeterminations() {
        if(!this.auth?.id) { return; }

        try {
            this.state.beginLoad();

            const responseDeterminations = await this.authService.loadDeterminationsByAuth(this.auth.id);
            this.determinations = responseDeterminations.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async finalizeAuth(): Promise<void> {
        if(!this.auth?.id) { return; }

        try {
            this.state.beginSave();
            const dataEntryComplete: boolean = await this.context.authDataComplete(this.auth.id);

            const saveAuth: UM.Auth = {
                id: this.auth.id,
                isPending: false,
                dataEntryComplete: dataEntryComplete || false
            }
            //check for DEC
            this.finalizeResponse = await this.authService.finalizeAuth(saveAuth);
            await this.context.refreshAuthSettings(this.auth);
            this.context.triggerCorrespondenceRules();

            this.auth = deepClone(this.context.auth);

        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endSave();
        }
    }

}
